/**
 * GPX File Service
 * Handles GPX file parsing using Web Worker for better memory management
 */

import { markRaw } from 'vue'

import { compressFileWithGzip } from '@/services/fileCompressionService'
import { generateId } from '@/services/idService'
import { showError } from '@/services/toastService'
import type { RoutePoint, Tour, Track, Waypoint } from '@/types/tour'
import { ensureImportedRoutePoints } from '@/utils/importRoutePointFallback'
import { truncateToMaxLength } from '@/utils/validation'

// Message types for Web Worker communication
interface WorkerProgressMessage {
  type: 'progress'
  progress: number
  message?: string
}

interface WorkerResultMessage {
  type: 'result'
  tour: {
    name: string
    description?: string
    tracks: Array<{
      name: string
      segments: Array<{
        points: Array<{
          lat: number
          lon: number
          ele?: number
          time?: number
          [key: string]: number | undefined
        }>
      }>
      metrics: {
        distance: number
        timeMoving: number | null
        timeElapsed: number | null
        elevationGain: number | null
        elevationLoss: number | null
        elevationMin: number | null
        elevationMax: number | null
      }
      startTime?: number
      endTime?: number
      type?: string
    }>
    waypoints: Array<{
      lat: number
      lon: number
      ele?: number
      name?: string
      desc?: string
      time?: number
      type?: string
    }>
    routePoints: Array<{
      lat: number
      lon: number
      ele?: number
      name?: string
      desc?: string
      order: number
      routeGroupIndex?: number
    }>
    distance: number
    time_moving: number | null
    time_elapsed: number | null
  }
}

interface WorkerErrorMessage {
  type: 'error'
  error: string
}

type WorkerMessage = WorkerProgressMessage | WorkerResultMessage | WorkerErrorMessage

/**
 * Create and manage a Web Worker for GPX file processing
 */
class GpxWorkerManager {
  private worker: Worker | null = null
  private processingCallback: ((progress: number, message?: string) => void) | null = null

  /**
   * Initialize the worker if not already created
   */
  private initWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL('../workers/gpxWorker.ts', import.meta.url), {
        type: 'module',
      })
    }
    return this.worker
  }

  /**
   * Process a GPX file using the Web Worker
   */
  async processFile(
    gpxContent: string,
    fileName: string,
    onProgress?: (progress: number, message?: string) => void,
  ): Promise<Omit<Tour, 'clientId'>> {
    return new Promise((resolve, reject) => {
      const worker = this.initWorker()
      this.processingCallback = onProgress || null

      // Set up message handler
      const messageHandler = (event: MessageEvent<WorkerMessage>) => {
        const message = event.data

        switch (message.type) {
          case 'progress':
            if (this.processingCallback) {
              this.processingCallback(message.progress, message.message)
            }
            break

          case 'result':
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)

            // Convert worker result to Tour format
            const tour = this.convertWorkerResultToTour(message.tour, fileName)
            resolve(tour)
            break

          case 'error':
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)
            reject(new Error(message.error))
            break
        }
      }

      // Set up error handler
      const errorHandler = (error: ErrorEvent) => {
        worker.removeEventListener('message', messageHandler)
        worker.removeEventListener('error', errorHandler)
        reject(new Error(`Worker error: ${error.message}`))
      }

      worker.addEventListener('message', messageHandler)
      worker.addEventListener('error', errorHandler)

      // Send data to worker
      worker.postMessage({
        type: 'parse',
        data: gpxContent,
        fileName: fileName,
      })
    })
  }

  /**
   * Process a GPX ArrayBuffer using the Web Worker (Transferable to avoid copy)
   */
  async processFileBuffer(
    gpxBuffer: ArrayBuffer,
    fileName: string,
    onProgress?: (progress: number, message?: string) => void,
  ): Promise<Omit<Tour, 'clientId'>> {
    return new Promise((resolve, reject) => {
      const worker = this.initWorker()
      this.processingCallback = onProgress || null

      const messageHandler = (event: MessageEvent<WorkerMessage>) => {
        const message = event.data

        switch (message.type) {
          case 'progress':
            if (this.processingCallback) {
              this.processingCallback(message.progress, message.message)
            }
            break
          case 'result':
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)
            const tour = this.convertWorkerResultToTour(message.tour, fileName)
            resolve(tour)
            break
          case 'error':
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)
            reject(new Error(message.error))
            break
        }
      }

      const errorHandler = (error: ErrorEvent) => {
        worker.removeEventListener('message', messageHandler)
        worker.removeEventListener('error', errorHandler)
        reject(new Error(`Worker error: ${error.message}`))
      }

      worker.addEventListener('message', messageHandler)
      worker.addEventListener('error', errorHandler)

      worker.postMessage(
        {
          type: 'parse',
          data: gpxBuffer,
          fileName: fileName,
        },
        [gpxBuffer as ArrayBuffer],
      )
    })
  }

  /**
   * Convert worker result to Tour format with proper IDs
   */
  private convertWorkerResultToTour(
    workerTour: WorkerResultMessage['tour'],
    fileName: string,
  ): Omit<Tour, 'clientId'> {
    // Process tracks
    const tracks: Track[] = workerTour.tracks.map((workerTrack) => {
      const track: Track = {
        clientId: generateId(),
        name: truncateToMaxLength(workerTrack.name, 255, 'Track name') || workerTrack.name,
        segments: workerTrack.segments.map((segment) => {
          const pts = segment.points as Array<{
            lat: number
            lon: number
            ele?: number
            time?: number
          }>
          for (let i = 0; i < pts.length; i++) {
            ;(pts[i] as unknown as Waypoint).clientId = generateId()
          }
          return {
            points: markRaw(pts) as unknown as Waypoint[],
          }
        }),
        visible: true,
        metrics: {
          ...workerTrack.metrics,
          elevationMin: workerTrack.metrics.elevationMin,
          elevationMax: workerTrack.metrics.elevationMax,
        },
        startTime: workerTrack.startTime,
        endTime: workerTrack.endTime,
        type: workerTrack.type,
      }

      return track
    })

    // Process waypoints
    const waypoints: Waypoint[] = workerTour.waypoints.map((workerWaypoint) => ({
      clientId: generateId(),
      lat: workerWaypoint.lat,
      lon: workerWaypoint.lon,
      ele: workerWaypoint.ele,
      name: workerWaypoint.name
        ? truncateToMaxLength(workerWaypoint.name, 255, 'Waypoint name')
        : undefined,
      description: workerWaypoint.desc
        ? truncateToMaxLength(workerWaypoint.desc, 2048, 'Waypoint description')
        : undefined,
      time: workerWaypoint.time,
      type: workerWaypoint.type,
    }))

    // Process route points
    const fallbackImportedTrackClientId = tracks[0]?.clientId
    if (!fallbackImportedTrackClientId && workerTour.routePoints.length > 0) {
      throw new Error('Imported GPX route points require at least one track')
    }
    const routePoints: RoutePoint[] = (workerTour.routePoints || []).map((rp) => ({
      clientId: generateId(),
      lat: rp.lat,
      lon: rp.lon,
      ele: rp.ele,
      name: rp.name ? truncateToMaxLength(rp.name, 255, 'Route point name') : undefined,
      description: rp.desc
        ? truncateToMaxLength(rp.desc, 2048, 'Route point description')
        : undefined,
      order: rp.order,
      trackClientId: tracks[rp.routeGroupIndex ?? 0]?.clientId ?? fallbackImportedTrackClientId!,
    }))
    const importedRoutePoints = ensureImportedRoutePoints(routePoints, tracks, generateId)

    // Create tour name
    const name =
      workerTour.name ||
      truncateToMaxLength(fileName.replace(/\.gpx$/i, ''), 255, 'Tour name') ||
      fileName.replace(/\.gpx$/i, '')

    return {
      name,
      description: workerTour.description,
      tracks,
      waypoints,
      routePoints: importedRoutePoints,
      distance: workerTour.distance,
      time_moving: workerTour.time_moving,
      time_elapsed: workerTour.time_elapsed,
    }
  }

  /**
   * Terminate the worker to free resources
   */
  terminate() {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
    this.processingCallback = null
  }
}

// Singleton instance
let workerManager: GpxWorkerManager | null = null

/**
 * Parse GPX file content provided as ArrayBuffer into tour data using Web Worker (transferable)
 * This avoids duplicating large strings in memory during structured cloning.
 */
export async function parseGPXFromArrayBuffer(
  gpxBuffer: ArrayBuffer,
  fileName: string,
  originalFile?: File,
  onProgress?: (progress: number, message?: string) => void,
): Promise<Omit<Tour, 'clientId'>> {
  try {
    const MAX_GPX_FILE_SIZE = 100 * 1024 * 1024
    if (gpxBuffer.byteLength > MAX_GPX_FILE_SIZE) {
      throw new Error(`GPX file too large. Maximum size is ${MAX_GPX_FILE_SIZE / (1024 * 1024)}MB`)
    }

    if (!workerManager) {
      workerManager = new GpxWorkerManager()
    }

    const tour = await workerManager.processFileBuffer(gpxBuffer, fileName, onProgress)

    tour.sourceFilename = truncateToMaxLength(originalFile?.name || fileName, 4096)

    if (originalFile) {
      const { file: compressedFile, compressed } = await compressFileWithGzip(originalFile)
      tour._originalFile = compressedFile
      tour.eventFileOriginalName = originalFile.name
      tour.eventFileName = compressedFile.name
      tour.eventFileMimeType = compressedFile.type
      tour.eventFileCompressed = compressed
    }

    return tour
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    showError(`Error parsing GPX file: ${message}`)
    throw new Error(`Error parsing GPX file: ${message}`)
  }
}
